<template>
  <div class="control-panel" :class="{ shadowed, 'edit-mode': editMode }">
    <template v-if="showRefreshButton">
      <button class="link btn mr-3" :title="appTranslate('Update')" @click="emit('refresh')">
        <i class="fa fa-refresh" />
      </button>
    </template>

    <span class="flags">
      <button
        v-for="culture of shownCultures"
        :key="culture.country + culture.language"
        class="link no-outline mb-1 mr-1 p-0"
        :class="{
          marked: (editMode && productHasCulturalData(product, culture)) || productInputHasCulturalData(culture),
          'has-changes': productInputHasCulturalData(culture),
          'available-for-country': editMode && (productHasCulturalData(product, culture) || productInputHasCulturalData(culture)),
        }"
        :title="getLocaleString(culture)"
        @click="emit('update:currentCulture', culture)"
      >
        <img
          :src="getFlag(culture.country)"
          :class="[availableInCountries.has(culture.country) ? 'flag' : 'small-flag', { faded: !isCurrentCulture(culture) }]"
        />
      </button>
    </span>

    <div class="d-flex flex-wrap ml-auto gap-2 align-items-center">
      <span v-if="selectedProductVersion" class="filter-active">{{ appTranslate('Viewing version %s', [selectedProductVersion]) }}</span>

      <template v-if="store.userPrivileges.has('manageProducts')">
        <!-- <button class="btn btn-outline-secondary" :class="{ 'filter-active': !!selectedProductVersion }" @click="emit('show-versions')">
          <i class="fa fa-code-fork" /> {{ appTranslate('Show version history') }}
        </button> -->

        <button v-if="store.isDevEnvironment" class="dev btn btn-warning mr-2" @click="console.log(clone(productInput || {}))">Print edit template</button>
        <template v-if="editMode">
          <button class="btn btn-warning" :disabled="saveDisabled" @click="emit('commit')">
            {{ appTranslate('Save') }}
          </button>
          <button class="btn btn-primary" :disabled="loading" @click="emit('cancel')">{{ appTranslate('Cancel') }}</button>
        </template>

        <button
          v-else
          class="link btn"
          :disabled="!!selectedProductVersion"
          :title="selectedProductVersion ? appTranslate('Cannot edit previous version. Select latest version to edit') : undefined"
          @click="emit('edit')"
        >
          <i class="fa fa-edit" />
        </button>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { clone, getLocaleString, removeEmptyObjects } from '@/utils/tools'

import { useTools } from '@/composables/useTools'
import { useTranslation } from '@/composables/useTranslation'
import { Culture } from '@/models/Global'
import { ProductInput } from '@/models/Import'
import { SimplifiedProduct } from '@/models/Product'
import { useDefaultStore } from '@/store'
import { productHasCulturalData } from '@/utils/productUtils'
import { computed, ref } from 'vue'

// -----------
// -- Setup --
// -----------
const props = defineProps<{
  editMode?: boolean
  currentCulture?: Culture
  product?: SimplifiedProduct
  saveDisabled?: boolean
  title?: string
  showRefreshButton?: boolean
  shadowed?: boolean
  productInput?: ProductInput
  loading?: boolean
  selectedProductVersion?: string
}>()

const emit = defineEmits<{
  edit: []
  commit: []
  cancel: []
  refresh: []
  'update:currentCulture': [culture: Culture]
  'show-versions': []
}>()

const store = useDefaultStore()
const { appTranslate } = useTranslation()
const { getFlag } = useTools()

// ---------------
// -- Reactives --
// ---------------

// --------------------
// -- Computed props --
// --------------------
const availableProductCultures = computed(() => {
  const productLanguages = new Set(Object.keys(props.product?.languageSpecific || {}))
  const productCountries = new Set(Object.keys(props.product?.countrySpecific || {}))
  return store.availableCultures.filter((culture) => productLanguages?.has(culture.language) || productCountries?.has(culture.country))
})

const shownCultures = computed(() => (props.editMode ? store.availableCultures : availableProductCultures.value))

const availableInCountries = computed(() => new Set(props.productInput?.availableInCountries ?? props.product?.availableInCountries))

// ---------------
// -- Functions --
// ---------------
const isCurrentCulture = (culture: Culture) => props.currentCulture?.language === culture.language && props.currentCulture.country === culture.country

const productInputHasCulturalData = (culture: Culture) => {
  const culturalData = [props.productInput?.countrySpecific?.[culture.country], props.productInput?.languageSpecific?.[culture.language]].filter((obj) => {
    if (obj) {
      const objCopy = clone(obj)
      removeEmptyObjects(objCopy)
      return !!Object.values(objCopy || {}).length
    }
  })

  return !!culturalData?.length
}
</script>

<style lang="scss" scoped>
.control-panel {
  display: flex;
  flex-wrap: wrap;
  position: sticky;
  bottom: 0;
  background-color: white;
  z-index: 2;
}

.shadowed {
  box-shadow: 0 -5px 7px -7px #bbb;
  // margin: 0.5em -0.5em -2em -0.5em;
  padding: 1em 2em !important;

  @media (min-width: 576px) {
    // margin: 0.5em -1em -2em -1em;
  }

  @media (min-width: 768px) {
    // margin: 0.5em -2em -2em -2em;
  }
}

button.marked {
  position: relative;
  margin-bottom: 0.5em;

  &::after {
    content: '\2022';
    position: absolute;
    font-size: 1.5em;
    bottom: 3px;
    left: 50%;
    transform: translate(-50%, 75%);
  }
}
.available-for-country::after {
  color: var(--primary);
}

.has-changes::after {
  color: yellowgreen;
}

.link i {
  font-size: 1.2em;
}

.flags img {
  margin-right: 0.35rem;
}
</style>
